import React from "react";
import TextArea, { type TextareaAutosizeProps } from "react-textarea-autosize";

import cx from "classnames";

import styles from "./css/TextInput.module.css";

type AnswerType = "singleline" | "multiline";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isTextValue(value: any): value is string {
	return typeof value === "string";
}

interface CommonProps {
	answerType: AnswerType;
	onChange?: (value: string, error?: string) => void;
	value: string;
}

type WithoutCommonProps<T extends CommonProps> = Omit<T, keyof CommonProps | "readonly">;

type TextAreaProps = {
	readonly?: false;
	error?: boolean;
} & CommonProps &
	Omit<TextareaAutosizeProps, "ref" | "readonly" | "onChange" | "value">;

type ParagraphProps = {
	readonly: true;
} & CommonProps &
	Omit<React.HTMLProps<HTMLParagraphElement>, "onChange" | "value" | "readonly">;

type Props = TextAreaProps | ParagraphProps;

export const MAX_LENGTH: { [k in AnswerType]: number } = {
	singleline: 1000,
	multiline: 3000,
};

export default class TextInput extends React.Component<Props, { text: string }> {
	static MAX_LENGTH = MAX_LENGTH;

	state = { text: this.props.value };

	componentDidUpdate(prevProps: Props) {
		const { readonly, value } = this.props;

		if (!readonly && value === "" && value !== prevProps.value) {
			// value is being reset manually (i.e, without an onChange event)
			// eslint-disable-next-line react/no-did-update-set-state
			this.setState({ text: "" });
		}
	}

	onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const { readonly, onChange } = this.props;

		if (!readonly) {
			const text = e.target.value;
			this.setState({ text });
			onChange?.(text);
		}
	};

	render() {
		// don't include onChange in rest of props (textarea props)
		const { readonly, answerType, onChange: _onChange, value: _value, ...props } = this.props;
		const { text } = this.state;

		if (readonly === true) {
			const { className, ...rest } = props as WithoutCommonProps<ParagraphProps>;

			return (
				<p className={cx(className, styles.paragraph)} {...rest}>
					{text}
				</p>
			);
		} else {
			const rest = props as WithoutCommonProps<TextAreaProps>;

			const { error, className, placeholder, ...textAreaProps } = rest;
			const defaultPlaceholder = `Responses can be ${MAX_LENGTH[answerType]} characters long.`;

			const multiplelines = answerType !== "singleline";

			return (
				<TextArea
					value={text}
					minRows={answerType === "singleline" ? 1 : 3}
					placeholder={placeholder || defaultPlaceholder}
					onChange={this.onChange}
					className={cx(
						styles.textarea,
						{ [styles.error]: error },
						{ [styles.multiplelines]: multiplelines },
						className,
					)}
					{...textAreaProps}
				/>
			);
		}
	}
}
